/* eslint-disable @typescript-eslint/no-explicit-any */
import { Endereco, EnderecoModel } from "@/components/endereco/core/models";
import { Topico, TopicoModel } from "@/components/topicos/core/models";

export interface Anexo {
  id: string;
  arquivo: string;
}

export interface Solicitacao {
  id: string;
  arquivada: boolean;
  topico: Topico;
  latitude: number;
  longitude: number;
  altitude: number;
  endereco?: Endereco;
  anexos: Anexo[];
  status: string;
  upn: string;
  totalApoiadores: number;
  dataCriacao: string;
  criadaPorMim: boolean;
  apoiando: boolean;
  estadoAprovacao: string;
}

export interface SolicitacaoMinima {
  id: string;
  status: string;
  anexos: Anexo[];
  apoiando: boolean;
  criadaPorMim: boolean;
}

export interface SolicitacaoCompacta {
  id: string;
  topico: Topico;
  status: string;
  endereco?: Endereco;
  anexos: Anexo[];
  upn: string;
  totalApoiadores: number;
  dataCriacao: string;
  apoiando: boolean;
  criadaPorMim: boolean;
}

export interface SolicitacaoParcial {
  id: string;
  topico?: Topico;
  upn?: string;
  arquivada: boolean;
  status: string;
  estadoAprovacao: string;
  dataCriacao: string;
  totalApoiadores: number;
  endereco?: Endereco;
  anexos: Anexo[];
}

// Classes utilitárias para conversão e manipulação
export class SolicitacaoModel {
  static empty(): Solicitacao {
    return {
      id: '',
      arquivada: false,
      topico: TopicoModel.empty(),
      latitude: 0,
      longitude: 0,
      altitude: 0,
      endereco: undefined,
      anexos: AnexoModel.listFromMap([]),
      status: '',
      upn: '',
      totalApoiadores: 0,
      dataCriacao: new Date(0).toISOString(),
      criadaPorMim: false,
      apoiando: false,
      estadoAprovacao: '',
    };
  }
  static fromMap(map: any): Solicitacao {
    return {
      id: map.id || '',
      arquivada: map.arquivada || false,
      topico: TopicoModel.fromMap(map.topico || {}),
      latitude: map.latitude || 0,
      longitude: map.longitude || 0,
      altitude: map.altitude || 0,
      endereco: map.endereco ? EnderecoModel.fromMap(map.endereco) : undefined,
      anexos: AnexoModel.listFromMap(map.anexos || []),
      status: map.status || '',
      upn: map.upn || '',
      totalApoiadores: map.totalApoiadores || 0,
      dataCriacao: map.dataCriacao ? new Date(map.dataCriacao).toISOString() : new Date(0).toISOString(),
      criadaPorMim: map.criadaPorMim || false,
      apoiando: map.apoiando || false,
      estadoAprovacao: map.estadoAprovacao || '',
    };
  }

  static listFromMap(list: unknown[]): Solicitacao[] {
    return list.length === 0 ? [] : list.map(item => SolicitacaoModel.fromMap(item));
  }
}

export class SolicitacaoMinimaModel {
  static empty(): SolicitacaoMinima {
    return {
      id: '',
      status: '',
      anexos: AnexoModel.listFromMap([]),
      apoiando: false,
      criadaPorMim: false,
    };
  }

  static fromMap(map: any): SolicitacaoMinima {
    return {
      id: map.id || '',
      status: map.status || '',
      anexos: AnexoModel.listFromMap(map.anexos || []),
      apoiando: map.apoiando || false,
      criadaPorMim: map.criadaPorMim || false,
    };
  }

  static listFromMap(list: unknown[]): SolicitacaoMinima[] {
    return list.length === 0 ? [] : list.map(item => SolicitacaoMinimaModel.fromMap(item));
  }
}

export class SolicitacaoCompactaModel {
  static empty(): SolicitacaoCompacta {
    return {
      id: '',
      topico: TopicoModel.empty(),
      status: '',
      endereco: undefined,
      anexos: AnexoModel.listFromMap([]),
      upn: '',
      totalApoiadores: 0,
      dataCriacao: new Date(0).toISOString(),
      apoiando: false,
      criadaPorMim: false,
    };
  }

  static fromMap(map: any): SolicitacaoCompacta {
    return {
      id: map.id || '',
      topico: TopicoModel.fromMap(map.topico || {}),
      status: map.status || '',
      endereco: map.endereco ? EnderecoModel.fromMap(map.endereco) : undefined,
      anexos: AnexoModel.listFromMap(map.anexos || []),
      upn: map.upn || '',
      totalApoiadores: map.totalApoiadores || 0,
      dataCriacao: (() => {
        const v = map.dataCriacao;
        if (!v) return new Date(0).toISOString();
        if (typeof v === 'string') {
          const d = new Date(v);
          return isNaN(d.getTime()) ? new Date(0).toISOString() : d.toISOString();
        }
        if (typeof v === 'number') {
          return new Date(v).toISOString();
        }
        return new Date(0).toISOString();
      })(),
      criadaPorMim: map.criadaPorMim || false,
      apoiando: map.apoiando || false,
    };
  }

  static listFromMap(list: unknown[]): SolicitacaoCompacta[] {
    return list.length === 0 ? [] : list.map(item => SolicitacaoCompactaModel.fromMap(item));
  }
}

export class SolicitacaoParcialModel {
  static empty(): SolicitacaoParcial {
    return {
      id: '',
      topico: undefined,
      upn: undefined,
      arquivada: false,
      status: '',
      estadoAprovacao: '',
      dataCriacao: new Date(0).toISOString(),
      totalApoiadores: 0,
      endereco: undefined,
      anexos: AnexoModel.listFromMap([]),
    };
  }

  static fromMap(map: any): SolicitacaoParcial {
    return {
      id: map.id || '',
      topico: map.topico ? TopicoModel.fromMap(map.topico) : undefined,
      upn: map.upn || undefined,
      arquivada: map.arquivada || false,
      status: map.status || '',
      estadoAprovacao: map.estadoAprovacao || '',
      dataCriacao: map.dataCriacao ? new Date(map.dataCriacao).toISOString() : new Date(0).toISOString(),
      totalApoiadores: map.totalApoiadores || 0,
      endereco: map.endereco ? EnderecoModel.fromMap(map.endereco) : undefined,
      anexos: AnexoModel.listFromMap(map.anexos || []),
    };
  }

  static listFromMap(list: unknown[]): SolicitacaoParcial[] {
    return list.length === 0 ? [] : list.map(item => SolicitacaoParcialModel.fromMap(item));
  }
}

export class AnexoModel {
  static empty(): Anexo {
    return {
      id: '',
      arquivo: '',
    };
  }

  static fromMap(map: any): Anexo {
    return {
      id: map.id || '',
      arquivo: map.arquivo || '',
    };
  }

  static listFromMap(list: unknown[]): Anexo[] {
    return list.length === 0 ? [] : list.map(item => AnexoModel.fromMap(item));
  }
}
